import React, { useEffect, useState } from "react";
import { Row, Col } from "react-bootstrap";
import { Link } from "react-router-dom";
import {
  FaBookOpen,
  FaCalendarAlt,
  FaClock,
  FaClipboardList,
  FaRocket,
} from "react-icons/fa";
import { ApiUrl } from "../../ApiUrl";
import "./Dashboard.css";

const quickActions = [
  {
    label: "Upload Assignment",
    icon: <FaClipboardList size={20} />,
    to: "/staff/assignment-entry",
    color: "#ef4444",
  },
  {
    label: "Lesson Plan",
    icon: <FaBookOpen size={20} />,
    to: "/staff/lesson-plan",
    color: "#3b82f6",
  },
];

const dayNameMap = {
  SUNDAY: "SUNDAY",
  MONDAY: "MONDAY",
  TUESDAY: "TUESDAY",
  WEDNESDAY: "WEDNESDAY",
  THURSDAY: "THURSDAY",
  FRIDAY: "FRIDAY",
  SATURDAY: "SATURDAY",
};

const backendOrigin = ApiUrl.apiurl.replace(/\/api\/?$/, "");
const resolveMediaUrl = (value) => {
  if (!value) return "";
  if (typeof value !== "string") return "";
  if (value.startsWith("http://") || value.startsWith("https://")) {
    return value;
  }
  if (value.startsWith("/")) {
    return `${backendOrigin}${value}`;
  }
  return `${backendOrigin}/${value}`;
};

const Dashboard = () => {
  const [teacherProfile, setTeacherProfile] = useState(null);
  const [profilePicError, setProfilePicError] = useState(false);
  const [timetableRows, setTimetableRows] = useState([]);
  const [attendanceSummary, setAttendanceSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const userId = sessionStorage.getItem("userId");
  const [sessionData, setSessionData] = useState({
    academicYearId: localStorage.getItem("academicSessionId"),
    orgId: localStorage.getItem("orgId"),
    branchId: localStorage.getItem("branchId")
  });

  const today = new Date();
  const todayDate = today.toISOString().split("T")[0];
  const todayName =
    dayNameMap[
      today.toLocaleDateString("en-US", {
        weekday: "long",
      }).toUpperCase()
    ] || "MONDAY";

  useEffect(() => {
    const handleSessionChange = () => {
      setSessionData({
        academicYearId: localStorage.getItem("academicSessionId"),
        orgId: localStorage.getItem("orgId"),
        branchId: localStorage.getItem("branchId")
      });
    };
    window.addEventListener("academicSessionChanged", handleSessionChange);
    return () => {
      window.removeEventListener("academicSessionChanged", handleSessionChange);
    };
  }, []);

  useEffect(() => {
    const fetchDashboardData = async () => {
      const { academicYearId, orgId, branchId } = sessionData;
      if (!academicYearId || !orgId || !branchId || !userId) {
        setLoading(false);
        return;
      }

      try {
        const teacherPromise = fetch(
          `${ApiUrl.apiurl}STAFF/registrationRetrieveUnified/${userId}/`
        ).then((response) => response.json());

        const timetablePromise = fetch(
          `${ApiUrl.apiurl}TIME_TABLE/GetSearchedTimeTableList/?academic_year_id=${academicYearId}&org_id=${orgId}&branch_id=${branchId}`
        ).then((response) => response.json());

        const attendancePromise = fetch(
          `${ApiUrl.apiurl}AttendanceDashBoard/dashboard/?academic_year_id=${academicYearId}&org_id=${orgId}&branch_id=${branchId}&date=${todayDate}`
        ).then((response) => response.json());

        const [teacherData, timetableData, attendanceData] = await Promise.all([
          teacherPromise,
          timetablePromise,
          attendancePromise,
        ]);

        const currentTeacher =
          teacherData?.data?.basicDetails || teacherData?.data || null;

        setProfilePicError(false);
        setTeacherProfile(currentTeacher);

        const teacherTimetable = Array.isArray(timetableData?.data)
          ? timetableData.data.filter(
              (row) => String(row.teacher_id) === String(userId)
            )
          : [];
        setTimetableRows(teacherTimetable);

        setAttendanceSummary(attendanceData?.data || null);
      } catch (error) {
        console.error("Error fetching staff dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [sessionData.academicYearId, sessionData.branchId, sessionData.orgId, todayDate, userId]);

  const todayClasses = timetableRows.filter(
    (row) => row.schedule_day?.toUpperCase() === todayName
  );
  const uniqueClassCount = new Set(
    timetableRows.map((row) => `${row.class_id}-${row.section_id}`)
  ).size;
  const uniqueSubjectCount = new Set(timetableRows.map((row) => row.subject_id))
    .size;
  const pendingClassCount = Math.max(timetableRows.length - todayClasses.length, 0);
  const teacherInitial =
    (teacherProfile?.employee_name || teacherProfile?.employeeName || teacherProfile?.first_name || "T")
      .charAt(0)
      .toUpperCase();
  const teacherDisplayName =
    teacherProfile?.employee_name ||
    teacherProfile?.employeeName ||
    [teacherProfile?.title, teacherProfile?.first_name, teacherProfile?.middle_name, teacherProfile?.last_name]
      .filter(Boolean)
      .join(" ") ||
    "Logged-in Staff";
  const teacherEmail =
    teacherProfile?.office_email || teacherProfile?.email || "-";
  const teacherRole = teacherProfile?.designationName || teacherProfile?.employee_type;
  const displayRole = teacherRole && teacherRole.toUpperCase() !== "DEFAULT" ? teacherRole : null;
  const teacherDepartment = teacherProfile?.departmentName;
  const displayDepartment = teacherDepartment && teacherDepartment.toUpperCase() !== "DEFAULT" ? teacherDepartment : null;
  const teacherJoinDate = teacherProfile?.date_of_joining || "-";
  const teacherProfilePic = resolveMediaUrl(
    teacherProfile?.profile_pic ||
      teacherProfile?.profile ||
      teacherProfile?.Profile_Pic ||
      teacherProfile?.profile_photo_path
  );

  return (
    <div className="container-fluid staff-dashboard-page">
      <Row>
        <Col>
          <div className="card p-0 staff-dashboard-shell">
            <div className="card-body p-3 p-lg-4">
              <Row className="mb-4">
                <Col md={4} className="mb-3 d-flex">
                  <div className="staff-dashboard-card staff-profile-card w-100">
                    <div className="staff-profile-header">
                      {teacherProfilePic && !profilePicError ? (
                        <img
                          src={teacherProfilePic}
                          alt={teacherDisplayName}
                          className="staff-profile-avatar"
                          style={{ objectFit: "cover" }}
                          onError={(event) => {
                            event.currentTarget.onerror = null;
                            setProfilePicError(true);
                          }}
                        />
                      ) : (
                        <div className="staff-profile-avatar">{teacherInitial}</div>
                      )}
                      <div className="staff-profile-meta">
                        <h5>{teacherDisplayName}</h5>
                        <p>{teacherEmail}</p>
                        {displayRole && <div className="staff-profile-role-chip">{displayRole}</div>}
                      </div>
                    </div>

                    <div className="staff-profile-footer">
                      {displayDepartment && <span>{displayDepartment}</span>}
                      <span>Joined: {teacherJoinDate}</span>
                    </div>
                  </div>
                </Col>

                <Col md={4} className="mb-3 d-flex">
                  <div className="staff-dashboard-card staff-overview-card w-100">
                    <div className="staff-card-title staff-overview-title">
                      <span className="staff-card-icon staff-card-icon-green">
                        <FaBookOpen />
                      </span>
                      <span>Total Classes</span>
                    </div>

                    <div className="staff-overview-main-value">
                      {loading ? "..." : timetableRows.length}
                    </div>
                    <div className="staff-overview-subtitle">
                      Scheduled for today
                    </div>

                    <div className="staff-overview-stats">
                      <div className="staff-overview-stat">
                        <FaClock />
                        <span>{loading ? "..." : pendingClassCount} Pending</span>
                      </div>
                      <div className="staff-overview-stat staff-overview-stat-center">
                        <span>{loading ? "..." : uniqueClassCount} Classes</span>
                      </div>
                      <div className="staff-overview-stat">
                        <FaBookOpen />
                        <span>{loading ? "..." : uniqueSubjectCount} Subjects</span>
                      </div>
                    </div>

                    <div className="staff-overview-footer">
                      Today&apos;s classes: {loading ? "..." : todayClasses.length}
                    </div>
                  </div>
                </Col>

                <Col md={4} className="mb-3 d-flex">
                  <div className="staff-dashboard-card staff-actions-card w-100">
                    <div className="staff-card-title">
                      <span className="staff-card-icon staff-card-icon-pink">
                        <FaRocket />
                      </span>
                      <span>Actions</span>
                    </div>

                    <div className="staff-actions-grid">
                      {quickActions.map((action) => (
                        <Link
                          key={action.label}
                          to={action.to}
                          className="staff-action-link"
                        >
                          <div className="staff-action-tile">
                            <div
                              className="staff-action-icon"
                              style={{ color: action.color }}
                            >
                              {action.icon}
                            </div>
                            <div className="staff-action-label">{action.label}</div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </Col>
              </Row>

              <Row>
                <Col xs={12} className="mb-3 d-flex">
                  <div className="staff-dashboard-card staff-classes-panel w-100">
                    <div className="staff-card-title">
                      <span className="staff-card-icon staff-card-icon-blue">
                        <FaCalendarAlt />
                      </span>
                      <span>Today&apos;s Classes</span>
                    </div>

                    {todayClasses.length > 0 ? (
                      <div className="staff-today-class-list">
                        {todayClasses.map((cls, index) => (
                          <div
                            key={`${cls.class_id}-${cls.section_id}-${cls.subject_id}-${index}`}
                            className="staff-today-class-row"
                          >
                            <div className="staff-today-class-main">
                              <div className="staff-today-class-subject">
                                {cls.subject || cls.subject_description || "-"}
                              </div>
                              <div className="staff-today-class-meta">
                                {cls.class} | {cls.section} |{" "}
                                {cls.teacher_name || teacherDisplayName}
                              </div>
                            </div>

                            <div className="staff-today-class-period">
                              <FaClock />
                              <span>{cls.class_period}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="staff-empty-state">
                        <div className="staff-empty-state-icon">
                          <FaCalendarAlt />
                        </div>
                        <div className="staff-empty-state-title">
                          No Classes Today
                        </div>
                        <div className="staff-empty-state-text">
                          You have no scheduled classes for today
                        </div>
                      </div>
                    )}

                    <div className="staff-classes-footer">
                      <Link to="/staff/staff-time-table" className="staff-timetable-link">
                        View Full Timetable
                      </Link>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
